/**
 * Benchmarks — list this client's AI Buyer Journey Benchmark runs and create
 * new ones. A run fans a neutral buyer-journey prompt library across a panel
 * of LLMs and reports how the company (vs. competitors) appears: No Mention /
 * Mention / Citation / Recommendation. Runs are immutable history — re-running
 * a company creates a new dated run.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  BenchmarkDepth,
  BenchmarkRunPurpose,
  BenchmarkRun,
  CompetitorGroup,
  CompetitorInput,
  PromptPersona,
} from '@aurora/shared';
import {
  COMPETITOR_GROUPS,
  COMPETITOR_GROUP_LABELS,
  PROMPT_ARCHITECTURE_LABELS,
  PROMPT_ARCHITECTURE_SHORT,
  PROMPT_ARCHITECTURES,
  PROMPT_PERSONAS,
  PROMPT_PERSONA_LABELS,
  SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE,
  SOURCE_INTELLIGENCE_DATASET_DEFAULT,
  type PromptArchitecture,
} from '@aurora/shared';
import { MarketSelect } from '../components/MarketSelect';
import { api, ApiClientError } from '../api/client';

/** One editable competitor row in the create form. */
interface CompetitorRow {
  value: string;
  group: CompetitorGroup;
  url: string;
}

const EMPTY_COMPETITOR_ROW: CompetitorRow = {
  value: '',
  group: 'supplier',
  url: '',
};
const MAX_COMPETITORS = 5;

/** Map benchmark statuses onto the existing pill palette. */
export function benchmarkPillClass(status: BenchmarkRun['status']): string {
  switch (status) {
    case 'draft':
    case 'pending':
      return 'pill pill-pending';
    case 'complete':
      return 'pill pill-complete';
    case 'failed':
      return 'pill pill-failed';
    default:
      return 'pill pill-processing';
  }
}

/** Parse a comma-separated text input into trimmed, non-empty values. */
function parseCsvInput(raw: string, max: number): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, max);
}

export function Benchmarks(): JSX.Element {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<BenchmarkRun[]>([]);
  // Current (PL-2 onward) vs the read-only PL-1 archive.
  const [library, setLibrary] = useState<'current' | 'archive'>('current');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New-benchmark form state.
  const [showForm, setShowForm] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [competitorRows, setCompetitorRows] = useState<CompetitorRow[]>([
    { ...EMPTY_COMPETITOR_ROW },
  ]);
  const [categories, setCategories] = useState('');
  const [applications, setApplications] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [personas, setPersonas] = useState<PromptPersona[]>([]);
  const [modelPanel, setModelPanel] = useState('');
  const [runsPerPrompt, setRunsPerPrompt] = useState(1);
  const [depth, setDepth] = useState<BenchmarkDepth>('full');
  const [webSearch, setWebSearch] = useState(true);
  // 7544ae15: a Source Intelligence run is one market of an industry dataset
  // and is always web-enabled and full depth (the server enforces it too).
  // Source Intelligence is the default while the 15-market PL-2 dataset is
  // being collected (Joan, 2026-10-21); a client benchmark is one click away.
  const [purpose, setPurpose] = useState<BenchmarkRunPurpose>('source_intelligence');
  const [datasetId, setDatasetId] = useState(SOURCE_INTELLIGENCE_DATASET_DEFAULT);
  const [datasetMarket, setDatasetMarket] = useState('');
  // Joan 2026-10-21: PL-1 = Aurora supplies the questions (pasted on the review
  // page); PL-2 = BLIP drafts them. The dataset id follows the architecture —
  // a dataset never mixes the two (the server refuses it too).
  const [architecture, setArchitecture] = useState<PromptArchitecture>('PL-2');
  const chooseArchitecture = (next: PromptArchitecture): void => {
    if (datasetId.trim() === SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE[architecture]) {
      setDatasetId(SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE[next]);
    }
    // A General (PL-1) run is a market of the General report: Source
    // Intelligence, not a client benchmark (the first one, 614f9519, was
    // created as a client benchmark by accident).
    if (next === 'PL-1') setPurpose('source_intelligence');
    setArchitecture(next);
  };
  const si = purpose === 'source_intelligence';
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // `isCurrent` drops a response that arrives after the tab changed, so a
  // slow "archive" fetch can never fill the "current" list.
  const load = async (isCurrent: () => boolean = () => true): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.listBenchmarks(library);
      if (isCurrent()) setRuns(page.items);
    } catch (e) {
      if (!isCurrent()) return;
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load benchmarks',
      );
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    let current = true;
    void load(() => current);
    return () => {
      current = false;
    };
  }, [library]);

  const updateRow = (i: number, patch: Partial<CompetitorRow>): void =>
    setCompetitorRows((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );
  const addRow = (): void =>
    setCompetitorRows((rows) =>
      rows.length >= MAX_COMPETITORS
        ? rows
        : [...rows, { ...EMPTY_COMPETITOR_ROW }],
    );
  const removeRow = (i: number): void =>
    setCompetitorRows((rows) => {
      const next = rows.filter((_, idx) => idx !== i);
      return next.length > 0 ? next : [{ ...EMPTY_COMPETITOR_ROW }];
    });
  const togglePersona = (p: PromptPersona): void =>
    setPersonas((current) =>
      current.includes(p)
        ? current.filter((x) => x !== p)
        : [...current, p],
    );

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!companyName.trim()) {
      setFormError('Company name is required');
      return;
    }
    // PL-2 §B: questions come only from an approved scope, never inferred.
    // PL-1 questions are Aurora's own, so no scope is needed to write them.
    if (
      architecture !== 'PL-1' &&
      parseCsvInput(categories, 20).length === 0 &&
      parseCsvInput(applications, 20).length === 0
    ) {
      setFormError('Add at least one product category or application.');
      return;
    }
    // Named here: the server's schema rejects a blank field before its own
    // dataset_required message can run, which reads as "Invalid benchmark request".
    if (si && !datasetId.trim()) {
      setFormError('Enter the dataset id for this Source Intelligence run.');
      return;
    }
    if (si && !datasetMarket.trim()) {
      setFormError('Choose the market this Source Intelligence run covers.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      // Drop empty rows; keep the group + optional website per competitor.
      const competitors: CompetitorInput[] = competitorRows
        .filter((r) => r.value.trim())
        .slice(0, MAX_COMPETITORS)
        .map((r) => ({
          value: r.value.trim(),
          group: r.group,
          ...(r.url.trim() ? { url: r.url.trim() } : {}),
        }));
      const { run } = await api.createBenchmark({
        companyName: companyName.trim(),
        ...(companyUrl.trim() ? { companyUrl: companyUrl.trim() } : {}),
        competitors,
        productCategories: parseCsvInput(categories, 20),
        applications: parseCsvInput(applications, 20),
        personas,
        scopeExclusions: parseCsvInput(exclusions, 20),
        ...(modelPanel.trim()
          ? { modelPanel: parseCsvInput(modelPanel, 8) }
          : {}),
        runsPerPrompt,
        webSearch: si ? true : webSearch,
        depth: si ? 'full' : depth,
        purpose,
        promptArchitecture: architecture,
        ...(si
          ? { datasetId: datasetId.trim(), datasetMarket: datasetMarket.trim() }
          : {}),
      });
      navigate(`/benchmarks/${run.id}`);
    } catch (err) {
      setFormError(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to create the benchmark',
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="benchmarks">
      <div className="page-head">
        <h1>AI Buyer Journey Benchmarks</h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Close' : 'New benchmark'}
        </button>
      </div>

      {showForm && (
        <form className="card" onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="bm-company">Company name *</label>
            <input
              id="bm-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Biomatik"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="bm-url">Company website</label>
            <input
              id="bm-url"
              type="url"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder="https://www.example.com"
            />
          </div>
          <div className="field">
            <label>Tracked competitors (up to 5)</label>
            <p className="field-hint">
              Name a competitor from any group — naming it tracks it, whatever
              its type. Competitors are tracked and scored only; they are never
              written into the questions (PL-2). Add a website to improve
              citation matching (needed for media brands not in our directory).
            </p>
            {competitorRows.map((row, i) => (
              <div className="competitor-row" key={i}>
                <input
                  aria-label={`Competitor ${i + 1} name or website`}
                  value={row.value}
                  onChange={(e) => updateRow(i, { value: e.target.value })}
                  placeholder="Name or website (e.g. R&D Systems / Bio-Techne)"
                />
                <select
                  aria-label={`Competitor ${i + 1} group`}
                  value={row.group}
                  onChange={(e) =>
                    updateRow(i, { group: e.target.value as CompetitorGroup })
                  }
                >
                  {COMPETITOR_GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {COMPETITOR_GROUP_LABELS[g]}
                    </option>
                  ))}
                </select>
                <input
                  aria-label={`Competitor ${i + 1} website (optional)`}
                  type="url"
                  value={row.url}
                  onChange={(e) => updateRow(i, { url: e.target.value })}
                  placeholder="Website (optional)"
                />
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => removeRow(i)}
                  aria-label={`Remove competitor ${i + 1}`}
                >
                  ✕
                </button>
              </div>
            ))}
            {competitorRows.length < MAX_COMPETITORS && (
              <button type="button" className="btn btn-small" onClick={addRow}>
                + Add competitor
              </button>
            )}
          </div>
          <div className="field">
            <label htmlFor="bm-categories">
              Product categories (comma-separated)
            </label>
            <input
              id="bm-categories"
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              placeholder="ELISA kits, recombinant proteins, custom antibodies"
            />
            {parseCsvInput(categories, 20).length >= 2 && (
              <p className="field-hint">
                Tip: for deeper prompts, consider running a separate benchmark
                per product category with its own competitor set — one prompt
                library covering all of these will go less deep on each.
              </p>
            )}
          </div>
          <div className="field">
            <label htmlFor="bm-applications">
              Applications (comma-separated)
            </label>
            <input
              id="bm-applications"
              value={applications}
              onChange={(e) => setApplications(e.target.value)}
              placeholder="Western blotting, Flow cytometry, Immunohistochemistry"
            />
            <p className="field-hint">
              The assays or techniques your buyers actually run — distinct from
              what you sell. Give at least one product category or application:
              PL-2 writes questions only from the approved scope and never
              infers it from the company.
            </p>
          </div>
          <div className="field">
            <label htmlFor="bm-exclusions">
              Excluded topics (comma-separated, optional)
            </label>
            <input
              id="bm-exclusions"
              value={exclusions}
              onChange={(e) => setExclusions(e.target.value)}
              placeholder="capillary Western, electrophoresis"
            />
            <p className="field-hint">
              Keeps a narrow scope narrow. A question that mentions an excluded
              topic is blocked before anything runs.
            </p>
          </div>
          <div className="field">
            <label>Persona(s) (optional)</label>
            <div className="persona-checkboxes">
              {PROMPT_PERSONAS.map((p) => (
                <label key={p} className="persona-checkbox">
                  <input
                    type="checkbox"
                    checked={personas.includes(p)}
                    onChange={() => togglePersona(p)}
                  />
                  {PROMPT_PERSONA_LABELS[p]}
                </label>
              ))}
            </div>
            <p className="field-hint">
              WHO is asking — changes a prompt's framing even when product and
              application stay the same. Leave all unchecked for
              persona-neutral questions.
            </p>
          </div>
          <div className="field">
            <label htmlFor="bm-models">
              Model panel (comma-separated OpenRouter slugs; empty = configured
              default)
            </label>
            <input
              id="bm-models"
              value={modelPanel}
              onChange={(e) => setModelPanel(e.target.value)}
              placeholder="openai/gpt-5.4-mini"
            />
          </div>
          <div className="filters">
            <label>
              Depth{' '}
              <select
                value={si ? 'full' : depth}
                onChange={(e) => setDepth(e.target.value as BenchmarkDepth)}
                disabled={si}
              >
                <option value="full">Full benchmark (25 questions)</option>
                <option value="teaser">Free teaser (5 prompts, 1 model)</option>
              </select>
            </label>
            <label>
              Runs per prompt{' '}
              <select
                value={runsPerPrompt}
                onChange={(e) => setRunsPerPrompt(Number(e.target.value))}
                disabled={depth === 'teaser'}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label title="Off = faster/cheaper, but only Perplexity returns sources; the other models answer from memory and cite nothing.">
              <input
                type="checkbox"
                checked={si ? true : webSearch}
                disabled={si}
                onChange={(e) => setWebSearch(e.target.checked)}
              />{' '}
              Web-enabled models{' '}
              <span className="muted">
                {si
                  ? '(required for Source Intelligence)'
                  : '(off = faster, but only Perplexity returns sources)'}
              </span>
            </label>
          </div>
          <div className="field">
            <label>Purpose</label>
            <div className="persona-checkboxes">
              <label className="persona-checkbox">
                <input
                  type="radio"
                  name="bm-purpose"
                  checked={!si}
                  onChange={() => setPurpose('client_benchmark')}
                />
                Client benchmark
              </label>
              <label className="persona-checkbox">
                <input
                  type="radio"
                  name="bm-purpose"
                  checked={si}
                  onChange={() => setPurpose('source_intelligence')}
                />
                Source Intelligence (industry dataset market)
              </label>
            </div>
            {si && (
              <div className="si-fields">
                <label htmlFor="bm-dataset">Dataset ID</label>
                <input
                  id="bm-dataset"
                  value={datasetId}
                  onChange={(e) => setDatasetId(e.target.value)}
                  placeholder={SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE[architecture]}
                />
                <label htmlFor="bm-market">Market</label>
                <MarketSelect
                  id="bm-market"
                  value={datasetMarket}
                  onChange={setDatasetMarket}
                />
                <p className="field-hint">
                  The market labels are the earlier study's, so each market
                  compares with its earlier run. Source Intelligence runs are always web-enabled and full depth, record
                  their research settings at approval, and need a coverage
                  review before pooling if citations come back thin.
                </p>
              </div>
            )}
          </div>
          <div className="field">
            <label>Questions</label>
            <div className="persona-checkboxes">
              {PROMPT_ARCHITECTURES.map((a) => (
                <label key={a} className="persona-checkbox">
                  <input
                    type="radio"
                    name="bm-architecture"
                    checked={architecture === a}
                    onChange={() => chooseArchitecture(a)}
                  />
                  {PROMPT_ARCHITECTURE_LABELS[a]}
                </label>
              ))}
            </div>
            <p className="field-hint">
              {architecture === 'PL-1'
                ? 'BLIP drafts nothing. The run opens as an empty draft: paste Aurora’s 25 questions (5 per stage) with Import questions, review, then approve.'
                : 'BLIP drafts 25 questions under the PL-2 rules for review and approval.'}
            </p>
          </div>
          {formError && <div className="form-error">{formError}</div>}
          <div className="head-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Generate questions for review'}
            </button>
          </div>
          <p className="field-hint">
            Nothing is sent to the AI model panel yet. BLIP drafts and checks the
            questions; an admin or manager reviews and approves them, and only
            then does the research run start.
          </p>
        </form>
      )}

      <div className="library-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={library === 'current'}
          className={`btn btn-small${library === 'current' ? ' btn-primary' : ''}`}
          onClick={() => setLibrary('current')}
        >
          Current
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={library === 'archive'}
          className={`btn btn-small${library === 'archive' ? ' btn-primary' : ''}`}
          onClick={() => setLibrary('archive')}
          title="Runs from before question review and approval existed (20 Oct 2026). Read-only."
        >
          Archive (before 20 Oct)
        </button>
      </div>
      {library === 'archive' && (
        <p className="field-hint">
          The archive: every benchmark produced before questions were
          reviewed and approved before research (20 Oct 2026). Their questions
          are General (PL-1). Reports and exports stay available and keep
          receiving source and supplier corrections, but no new work is done on
          them — create a new benchmark instead (General or Technical).
        </p>
      )}
      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div className="muted centered">Loading benchmarks…</div>
      ) : runs.length === 0 ? (
        <div className="empty-state muted">
          No benchmark runs yet. Create one to see how AI systems present a
          company across the scientific buyer journey.
        </div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Models</th>
              <th>Depth</th>
              <th>Questions</th>
              <th>Status</th>
              <th>Cost</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id}>
                <td className="lead-name">{run.companyName}</td>
                <td>{run.modelPanel.join(', ')}</td>
                <td>{run.conditions.depth}</td>
                <td>
                  {/* Portal logins don't receive the architecture (internal
                      study plumbing): show nothing rather than guess. */}
                  {run.promptArchitecture
                    ? PROMPT_ARCHITECTURE_SHORT[run.promptArchitecture]
                    : '—'}
                  {run.promptArchitecture && (run.promptLibraryRevision ?? 1) > 1
                    ? ` · rev ${run.promptLibraryRevision}`
                    : ''}
                  {run.datasetMarket ? (
                    <div className="muted">{run.datasetMarket}</div>
                  ) : null}
                </td>
                <td>
                  <span className={benchmarkPillClass(run.status)}>
                    {run.status}
                  </span>
                </td>
                <td>
                  {run.usage ? `$${run.usage.total.cost.toFixed(2)}` : '—'}
                </td>
                <td>{new Date(run.createdAt).toLocaleDateString()}</td>
                <td>
                  <Link
                    to={`/benchmarks/${run.id}`}
                    className="btn btn-small btn-view"
                  >
                    {run.status === 'draft' || run.status === 'drafting'
                      ? 'Review questions'
                      : 'View report'}{' '}
                    <span className="arrow" aria-hidden>
                      →
                    </span>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
